// Helpers for reading/writing auth data in localStorage.
// Access token is never persisted — it lives only in the Zustand store (in-memory).
// Refresh token is persisted to survive page refreshes.

import { STORAGE_KEYS } from '@/constants/auth';
import type { AdminUser } from '@/types/auth';

const isBrowser = typeof window !== 'undefined';

// ── Refresh token ──

export function getStoredRefreshToken(): string | null {
  if (!isBrowser) return null;
  return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
}

export function setStoredRefreshToken(token: string): void {
  if (!isBrowser) return;
  localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
}

export function clearStoredRefreshToken(): void {
  if (!isBrowser) return;
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
}

// ── Admin profile cache ──
// Cached to avoid a GET /me on every page refresh; always validated against the API on init.

export function getStoredAdmin(): AdminUser | null {
  if (!isBrowser) return null;
  const raw = localStorage.getItem(STORAGE_KEYS.ADMIN_DATA);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AdminUser;
  } catch {
    return null;
  }
}

export function setStoredAdmin(admin: AdminUser): void {
  if (!isBrowser) return;
  localStorage.setItem(STORAGE_KEYS.ADMIN_DATA, JSON.stringify(admin));
}

export function clearStoredAdmin(): void {
  if (!isBrowser) return;
  localStorage.removeItem(STORAGE_KEYS.ADMIN_DATA);
}

// ── Bulk clear ──

export function clearAllAuthStorage(): void {
  clearStoredRefreshToken();
  clearStoredAdmin();
}
